/**
 * 首页 - 重构版
 * 支持从API获取案例数据
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCases } from '../services/api';
import { CaseItem } from '../types';

// 颜色映射
const colorMap: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
};

const Home: React.FC = () => {
  const [cases, setCases] = useState<CaseItem[]>([]);

  useEffect(() => {
    const fetchCases = async () => {
      try {
        const result = await getCases();
        if (result.code === 0 && result.data) {
          setCases(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch cases:', error);
      }
    };
    fetchCases();
  }, []);

  return (
    <div className="animate-in fade-in duration-700">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-24 px-4 bg-[#0a1128] text-white">
        {/* Abstract background elements */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] -mr-64 -mt-64"></div>
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-emerald-600/10 rounded-full blur-[80px] -ml-32 -mb-32"></div>

        <div className="max-w-6xl mx-auto relative z-10 flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-blue-300 text-sm font-medium mb-8">
              <span className="flex h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
              AI 场景大师 · 创新实践赛报名专属通道
            </div>
            <h1 className="text-4xl sm:text-6xl font-extrabold leading-[1.15] mb-8 tracking-tight">
              赋能教育场景 <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-white to-emerald-400">
                定义智能未来
              </span>
            </h1>
            <p className="text-lg text-slate-400 mb-10 max-w-xl font-light leading-relaxed">
              本次 OPC 创新实践赛由 AI 场景大师提供深度技术支持。欢迎各位学子在这里开启你的智能体创新之旅，利用领先的 AI 技术解决真实教育场景中的难题。
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start">
              <Link to="/register" className="bg-blue-600 text-white px-10 py-4 rounded-xl font-bold shadow-2xl shadow-blue-900/40 hover:bg-blue-500 transition-all text-center">
                立即报名参赛
              </Link>
              <a href="https://aimaster.aiscenes.cn/" target="_blank" rel="noreferrer" className="bg-white/5 backdrop-blur-md text-white border border-white/10 px-10 py-4 rounded-xl font-bold hover:bg-white/10 transition-all text-center">
                平台试用入口
              </a>
            </div>
          </div>

          {/* Right illustration / Branding */}
          <div className="flex-1 w-full max-w-lg">
             <div className="relative p-1 bg-gradient-to-br from-blue-500/30 to-emerald-500/10 rounded-[40px]">
                <div className="bg-[#0f1a3a] rounded-[38px] p-10 border border-white/10 shadow-2xl overflow-hidden relative">
                   <div className="absolute top-0 right-0 p-6 opacity-20">
                      <img 
                        src="https://aimaster.aiscenes.cn/assets/images/AIscenes-logo-BEENeMHy.png" 
                        alt="" 
                        className="h-6 brightness-0 invert"
                      />
                   </div>
                   <div className="flex flex-col gap-6">
                      <div className="h-2 w-16 bg-blue-500 rounded-full"></div>
                      <div className="space-y-3">
                        <div className="h-4 bg-white/5 rounded w-full"></div>
                        <div className="h-4 bg-white/5 rounded w-5/6"></div>
                        <div className="h-4 bg-white/5 rounded w-4/6"></div>
                      </div>
                      <div className="p-6 bg-blue-600/10 border border-blue-500/20 rounded-2xl flex items-center gap-4">
                         <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-2xl">🤖</div>
                         <div>
                            <div className="text-white font-bold text-sm">OPC 智能体助手</div>
                            <div className="text-blue-300/60 text-xs tracking-wide uppercase mt-0.5">Ready to Assist</div>
                         </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="h-12 bg-white/5 rounded-xl border border-white/10"></div>
                        <div className="h-12 bg-white/5 rounded-xl border border-white/10"></div>
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* Agents Showcase */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">教育智能体案例展示</h2>
              <p className="text-slate-500 leading-relaxed text-lg">
                基于 AI 场景大师平台开发的优秀作品，涵盖从理科模拟到人文创作的多元应用场景。
              </p>
            </div>
            <a href="https://aimaster.aiscenes.cn/" target="_blank" rel="noreferrer" className="text-blue-600 font-bold flex items-center gap-2 hover:gap-3 transition-all">
              探索更多案例 <span>→</span>
            </a>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {cases.map((c, i) => {
              const colors = colorMap[c.color] || colorMap.blue;
              return (
                <div key={c.id || i} className="group flex flex-col bg-slate-50 p-8 rounded-[32px] border border-transparent hover:border-blue-100 hover:bg-white hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-300">
                  <div className={`w-14 h-14 ${colors.bg} ${colors.text} rounded-2xl flex items-center justify-center mb-6 text-3xl group-hover:scale-110 transition-transform shadow-sm`}>
                    {c.icon}
                  </div>
                  <div className="mb-2">
                    <span className="px-2 py-0.5 rounded-md bg-white text-[10px] font-bold text-slate-400 uppercase tracking-widest border border-slate-100">{c.tag}</span>
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-slate-800">{c.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-grow">
                    {c.description}
                  </p>
                  <div className="pt-4 border-t border-slate-200/50">
                    <span className="text-xs font-bold text-blue-600">Case #{String(i+1).padStart(2, '0')}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Box */}
          <div className="mt-20 relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-800 rounded-[40px] p-8 md:p-16 text-white shadow-2xl">
            <div className="absolute top-0 right-0 opacity-10 -mr-20 -mt-20">
              <img src="https://aimaster.aiscenes.cn/assets/images/AIscenes-logo-BEENeMHy.png" className="w-[400px] brightness-0 invert" alt="" />
            </div>
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-10">
              <div className="max-w-xl text-center md:text-left">
                <h3 className="text-3xl font-extrabold mb-4">创造你自己的教育智能体</h3>
                <p className="text-blue-100/80 text-lg">
                  不仅局限于现有的案例，通过 AI 场景大师平台，每个人都能将创意转化为实用的 AI 教学工具。
                </p>
              </div>
              <a 
                href="https://aimaster.aiscenes.cn/" 
                target="_blank" 
                rel="noreferrer" 
                className="bg-white text-blue-700 px-12 py-5 rounded-2xl font-black text-lg hover:bg-blue-50 transition-all shadow-xl active:scale-95 whitespace-nowrap"
              >
                开始创造吧
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Competition Info CTA */}
      <section className="bg-slate-50 py-24 px-4 border-t border-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-2 bg-blue-600 mx-auto mb-10 rounded-full"></div>
          <h2 className="text-4xl font-extrabold text-slate-900 mb-8">准备好展示你的创新力了吗？</h2>
          <p className="text-slate-600 mb-12 text-xl max-w-2xl mx-auto leading-relaxed">
            OPC 创新实践赛正等待着像你一样充满想象力的开发者。通过本专属通道报名，获得优先技术审核。
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/register" className="bg-blue-600 text-white px-14 py-5 rounded-2xl font-bold shadow-xl shadow-blue-200 hover:bg-blue-700 hover:-translate-y-1 transition-all">
              立即前往报名表
            </Link>
            <Link to="/details" className="bg-white text-slate-700 border border-slate-200 px-14 py-5 rounded-2xl font-bold hover:bg-slate-50 transition-all">
              查看实施方案
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
